import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input", "status", "companyName", "companyAddress", "mismatchBanner", "verifiedBadge",
    "billingName", "billingAddressLine1", "billingAddressLine2", "billingCity", "billingPostalCode"
  ]
  static values = {
    url: String,
    csrfToken: String,
    debounceMs: { type: Number, default: 600 }
  }

  connect() {
    this.abortController = null
    this.debounceTimer = null
    this.viesData = null
  }

  disconnect() {
    this.clearDebounce()
    this.abortInflight()
  }

  lookup() {
    this.clearDebounce()

    const value = this.inputTarget.value.trim()

    if (value.length < 4) {
      this.clearStatus()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.performLookup(value)
    }, this.debounceMsValue)
  }

  async performLookup(vatNumber) {
    this.abortInflight()
    this.showLoading()

    this.abortController = new AbortController()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({ vat_number: vatNumber }),
        signal: this.abortController.signal
      })

      if (response.status === 429) {
        this.showWarning("Too many requests. Please wait a moment.")
        return
      }

      const data = await response.json()

      if (data.valid) {
        this.showValid(data)
      } else if (data.service_unavailable) {
        this.showWarning(data.error || "VAT validation service is temporarily unavailable.")
      } else {
        this.showInvalid(data.error || "VAT number is not valid.")
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this.showWarning("Could not validate VAT number. Please try again later.")
      }
    }
  }

  showLoading() {
    this.statusTarget.innerHTML =
      '<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Checking VAT number...'
    this.statusTarget.className = "vat-status mt-1 text-muted"
    this.clearCompanyInfo()
    this.hideMismatchBanner()
  }

  showValid(data) {
    this.statusTarget.innerHTML =
      '<i class="bi bi-check-circle-fill text-success me-1"></i> Valid VAT number'
    this.statusTarget.className = "vat-status mt-1"

    if (this.hasCompanyNameTarget && data.name) {
      this.companyNameTarget.textContent = data.name
    }
    if (this.hasCompanyAddressTarget && data.address) {
      this.companyAddressTarget.textContent = data.address
    }

    this.showVerifiedBadge()
    this.viesData = { name: data.name, address: data.address }
    this.checkMismatch()
  }

  showInvalid(message) {
    this.statusTarget.innerHTML =
      `<i class="bi bi-x-circle-fill text-danger me-1"></i> ${this.escapeHtml(message)}`
    this.statusTarget.className = "vat-status mt-1"
    this.clearCompanyInfo()
    this.hideMismatchBanner()
  }

  showWarning(message) {
    this.statusTarget.innerHTML =
      `<i class="bi bi-exclamation-triangle-fill text-warning me-1"></i> ${this.escapeHtml(message)}`
    this.statusTarget.className = "vat-status mt-1"
    this.clearCompanyInfo()
    this.hideMismatchBanner()
  }

  clearStatus() {
    this.statusTarget.innerHTML = ""
    this.statusTarget.className = "vat-status mt-1"
    this.clearCompanyInfo()
    this.hideMismatchBanner()
  }

  clearCompanyInfo() {
    if (this.hasCompanyNameTarget) this.companyNameTarget.textContent = ""
    if (this.hasCompanyAddressTarget) this.companyAddressTarget.textContent = ""
    this.hideVerifiedBadge()
  }

  showVerifiedBadge() {
    if (this.hasVerifiedBadgeTarget) {
      this.verifiedBadgeTarget.innerHTML =
        '<span class="badge text-success-emphasis bg-success-subtle border border-success-subtle ms-1"><i class="bi bi-patch-check-fill me-1"></i>VIES Verified</span>'
      this.verifiedBadgeTarget.classList.remove("d-none")
    }
  }

  hideVerifiedBadge() {
    if (this.hasVerifiedBadgeTarget) {
      this.verifiedBadgeTarget.innerHTML = ""
      this.verifiedBadgeTarget.classList.add("d-none")
    }
  }

  // --- Mismatch detection ---

  checkMismatch() {
    if (!this.viesData) return
    if (!this.hasMismatchBannerTarget) return

    const nameMismatch = this.detectNameMismatch()
    const addressMismatch = this.detectAddressMismatch()

    if (nameMismatch || addressMismatch) {
      this.showMismatchBanner(nameMismatch, addressMismatch)
    } else {
      this.hideMismatchBanner()
    }
  }

  detectNameMismatch() {
    if (!this.hasBillingNameTarget) return false
    const formName = this.billingNameTarget.value.trim()
    if (!formName || !this.viesData.name) return false
    return !this.fuzzyMatch(formName, this.viesData.name)
  }

  detectAddressMismatch() {
    if (!this.hasBillingAddressLine1Target) return false
    if (!this.viesData.address) return false

    const parts = [
      this.hasBillingAddressLine1Target ? this.billingAddressLine1Target.value : "",
      this.hasBillingAddressLine2Target ? this.billingAddressLine2Target.value : "",
      this.hasBillingPostalCodeTarget ? this.billingPostalCodeTarget.value : "",
      this.hasBillingCityTarget ? this.billingCityTarget.value : ""
    ]
    const formAddress = parts.join(" ").trim()
    if (!formAddress) return false
    return !this.fuzzyMatch(formAddress, this.viesData.address)
  }

  fuzzyMatch(a, b) {
    const normalize = (s) => s.toLowerCase().replace(/\s+/g, " ").trim()
    return normalize(a) === normalize(b)
  }

  showMismatchBanner(nameMismatch, addressMismatch) {
    const details = []
    if (nameMismatch) details.push(`<strong>Name:</strong> ${this.escapeHtml(this.viesData.name)}`)
    if (addressMismatch) details.push(`<strong>Address:</strong> ${this.escapeHtml(this.viesData.address)}`)

    this.mismatchBannerTarget.innerHTML = `
      <div class="alert alert-warning mb-0 py-2 small">
        <i class="bi bi-info-circle me-1"></i>
        VIES returned different details than your billing form:
        <div class="mt-1">${details.join("<br>")}</div>
        <div class="mt-2">
          <button type="button" class="btn btn-sm btn-outline-warning me-1" data-action="vat-lookup#applyViesDetails">
            Use VIES details
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" data-action="vat-lookup#dismissMismatch">
            Keep my details
          </button>
        </div>
      </div>
    `
    this.mismatchBannerTarget.classList.remove("d-none")
  }

  hideMismatchBanner() {
    if (this.hasMismatchBannerTarget) {
      this.mismatchBannerTarget.innerHTML = ""
      this.mismatchBannerTarget.classList.add("d-none")
    }
  }

  applyViesDetails() {
    if (!this.viesData) return

    if (this.viesData.name && this.hasBillingNameTarget) {
      this.billingNameTarget.value = this.viesData.name
    }
    if (this.viesData.address && this.hasBillingAddressLine1Target) {
      this.billingAddressLine1Target.value = this.viesData.address
      if (this.hasBillingAddressLine2Target) {
        this.billingAddressLine2Target.value = ""
      }
    }

    this.hideMismatchBanner()
  }

  dismissMismatch() {
    this.hideMismatchBanner()
  }

  // --- Helpers ---

  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  abortInflight() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
